import { Link, NavLink, useLocation } from "react-router-dom";
import { LOGO_ICON } from "@/lib/api";
import { Sparkles, Youtube } from "lucide-react";

const navItems = [
  { to: "/", label: "Home", testId: "nav-home" },
  { to: "/why", label: "Why Techno Sort", testId: "nav-why" },
  { to: "/pricing", label: "Pricing", testId: "nav-pricing" },
  { to: "/download", label: "Download", testId: "nav-download" },
  { to: "/contact", label: "Contact", testId: "nav-contact" },
];

function Header() {
  return (
    <header
      className="sticky top-0 z-50 border-b border-white/10 backdrop-blur-xl bg-black/60"
      data-testid="site-header"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3" data-testid="brand-link">
          <img src={LOGO_ICON} alt="Techno Sort" className="w-9 h-9 object-contain rounded-sm bg-black/40 ring-1 ring-white/10" />
          <div className="leading-tight">
            <div className="font-display font-bold tracking-tight text-white text-lg">TECHNO SORT</div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-neutral-500">Intelligent Document Automation</div>
          </div>
        </Link>

        <nav className="hidden md:flex items-center gap-8">
          {navItems.map(n => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={n.testId}
              className={({ isActive }) =>
                `text-sm tracking-wide link-underline ${isActive ? "text-[#F4A22A]" : "text-neutral-300 hover:text-white"}`
              }
              end
            >
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <a
            href="https://www.youtube.com/@Techno_Sort-RD"
            target="_blank" rel="noreferrer"
            data-testid="header-youtube-link"
            className="hidden sm:inline-flex items-center justify-center w-9 h-9 border border-white/15 hover:border-[#C8372D] hover:text-[#C8372D] text-neutral-300 transition-colors"
            aria-label="Techno Sort YouTube tutorials"
            title="Tutorials on YouTube"
          >
            <Youtube className="w-4 h-4" />
          </a>
          <Link
            to="/pricing"
            data-testid="header-buy-cta"
            className="btn-primary rounded-none px-5 py-2.5 text-sm font-bold uppercase tracking-wider"
          >
            Buy License
          </Link>
        </div>
      </div>
    </header>
  );
}

function Footer() {
  const year = new Date().getFullYear();
  return (
    <footer className="border-t border-white/10" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-16 grid md:grid-cols-5 gap-12">
        <div className="md:col-span-2">
          <div className="flex items-center gap-3 mb-4">
            <img src={LOGO_ICON} className="w-10 h-10 rounded-sm ring-1 ring-white/10" alt="logo" />
            <div>
              <div className="font-display font-bold text-xl tracking-tight">TECHNO SORT</div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-neutral-500">Intelligent Document Automation</div>
            </div>
          </div>
          <p className="text-neutral-400 text-sm max-w-md leading-relaxed">
            Intelligent document automation for offices that drown in PDFs. Runs offline.
            Pay once. Use forever.
          </p>
        </div>

        <div>
          <div className="text-xs tracking-[0.2em] uppercase text-neutral-500 mb-4">Product</div>
          <ul className="space-y-2 text-sm">
            <li><Link to="/why" className="text-neutral-300 hover:text-white">Why Techno Sort</Link></li>
            <li><Link to="/pricing" className="text-neutral-300 hover:text-white">Pricing</Link></li>
            <li><Link to="/download" className="text-neutral-300 hover:text-white">Download</Link></li>
          </ul>
        </div>

        <div>
          <div className="text-xs tracking-[0.2em] uppercase text-neutral-500 mb-4">Support</div>
          <ul className="space-y-2 text-sm">
            <li><Link to="/contact" className="text-neutral-300 hover:text-white">Contact</Link></li>
            <li><a className="text-neutral-300 hover:text-white" href="mailto:technosort.rd@gmail.com">technosort.rd@gmail.com</a></li>
            <li>
              <a
                href="https://www.youtube.com/@Techno_Sort-RD"
                target="_blank" rel="noreferrer"
                data-testid="footer-youtube-link"
                className="text-neutral-300 hover:text-white inline-flex items-center gap-2"
              >
                <Youtube className="w-4 h-4 text-[#C8372D]" /> Tutorials on YouTube
              </a>
            </li>
          </ul>
        </div>

        <div>
          <div className="text-xs tracking-[0.2em] uppercase text-neutral-500 mb-4">Legal</div>
          <ul className="space-y-2 text-sm">
            <li><Link to="/privacy"  className="text-neutral-300 hover:text-white" data-testid="footer-privacy-link">Privacy Policy</Link></li>
            <li><Link to="/terms"    className="text-neutral-300 hover:text-white" data-testid="footer-terms-link">Terms &amp; Conditions</Link></li>
            <li><Link to="/refund"   className="text-neutral-300 hover:text-white" data-testid="footer-refund-link">Refund Policy</Link></li>
            <li><Link to="/shipping" className="text-neutral-300 hover:text-white" data-testid="footer-shipping-link">Shipping &amp; Delivery</Link></li>
            <li><Link to="/eula"     className="text-neutral-300 hover:text-white" data-testid="footer-eula-link">EULA</Link></li>
          </ul>
        </div>
      </div>
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-6 flex flex-col md:flex-row gap-3 items-center justify-between text-xs text-neutral-500">
          <div>© {year} Techno Analyst R&amp;D. All rights reserved.</div>
          <div className="flex gap-6">
            <span>One license · One PC</span>
            <span>USD pricing</span>
            <span>Razorpay secured</span>
          </div>
        </div>
      </div>
    </footer>
  );
}

export default function Layout({ children }) {
  const loc = useLocation();
  // Scroll to top on route change
  if (typeof window !== "undefined") {
    setTimeout(() => window.scrollTo({ top: 0, behavior: "instant" }), 0);
  }
  return (
    <div className="relative" data-page={loc.pathname}>
      <Header />
      <main className="relative z-10">{children}</main>
      <Footer />
    </div>
  );
}
